import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download, Loader2, Pencil, FileVideo, FileAudio, SlidersHorizontal, AlertTriangle, RotateCw, Info, Sparkles, MessageSquare, Network } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  useReport,
  useJobs,
  usePromptReports,
  gridReports,
  usePromptReport,
  useGenerateSentiment,
  useJobInfo,
  useRegenerateReport,
  useRefineContext,
  useRenameJob,
  PROMPT_REPORT_LABELS,
  type PromptReportType,
  type Persona,
} from "@/lib/api";
import { useAvailableVariants, useSetPersona } from "@/entities/persona";
import { PersonaStrip } from "@/widgets/persona-strip";
import { PersonaInvite } from "@/features/add-persona";
import { FileMetadataStrip } from "./FileMetadataStrip";
import { ProjectInfoModal } from "./ProjectInfoModal";
import { getPersonaTabs } from "@/lib/personaTabs";
import { Markdown } from "@/shared/ui/Markdown";
import { CopyButton } from "@/components/CopyButton";
import { DOCXButton } from "@/components/DOCXButton";
import { PDFButton } from "@/components/PDFButton";
import { applySpeakerNames } from "@/lib/speakerNames";
import { getProjectName } from "@/lib/projectName";
import { getProjectMeta, setProjectMeta } from "@/lib/projectMeta";
import {
  downloadReport,
  downloadReportDocx,
  downloadJobSubtitles,
  downloadAllAsZip,
  getFormatsForType,
  type DownloadFormat,
  type ReportForDownload,
} from "@/lib/download";
import { API_BASE_URL } from "@/lib/config";
import { tokenStorage } from "@/lib/tokenStorage";
import { getStepLabel, getStepProgress } from "@/lib/processingSteps";
import { describeReportFailure } from "@/lib/reportFailureMessage";
import { FunnyLoader } from "./FunnyLoader";
import { RefinePanel } from "@/components/RefinePanel";
import { ChatPanel } from "@/components/ChatPanel";
import { ProjectTreePanel } from "@/components/ProjectTreePanel";

interface ProjectViewProps {
  position: number;
}

function formatDate(dateString: string) {
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
}

function pillHtml(tone: PillTone, text: string): string {
  const colors: Record<PillTone, string> = {
    green: "background:#dcfce7;color:#166534;",
    amber: "background:#fef3c7;color:#92400e;",
    red: "background:#fee2e2;color:#991b1b;",
    gray: "background:#f3f4f6;color:#374151;",
  };
  return `<span class="pill" style="${colors[tone]}">${text}</span>`;
}

function scoreGridHtml(obj: Record<string, unknown>, pairs: Array<[string, string]>): string {
  return `<div class="grid">${pairs
    .map(([key, label]) => {
      const raw = obj[key];
      const numVal = typeof raw === "number" ? raw : null;
      const isPercent = numVal !== null && numVal >= 0 && numVal <= 100;
      const barHtml = isPercent
        ? `<div style="margin-top:4px;height:4px;border-radius:2px;background:#e5e7eb;overflow:hidden;width:70%;margin-left:auto;margin-right:auto;">
            <div style="height:100%;border-radius:2px;background:#818cf8;width:${numVal}%"></div>
          </div>`
        : "";
      return `<div class="metric"><div class="m-label">${label}</div><div class="m-value">${String(raw ?? "—")}</div>${barHtml}</div>`;
    })
    .join("")}</div>`;
}

function focusEditingHtml(data: Record<string, unknown>): string {
  const bestClips = (data.best_clips ?? []) as Array<Record<string, unknown>>;
  const deadZones = (data.dead_zones ?? []) as Array<Record<string, unknown>>;
  const energyPeaks = (data.energy_peaks ?? []) as Array<Record<string, unknown>>;
  const thumb = data.suggested_thumbnail_moment as Record<string, unknown> | undefined;

  const clipsHtml = bestClips
    .map(
      (c, i) => `
      <div class="item-card">
        <div class="item-head">
          <strong>#${c.rank ?? i + 1} · ${c.title ?? ""}</strong>
          ${pillHtml(viralityTone(Number(c.virality_score ?? 0)), `Virality ${c.virality_score ?? "—"}`)}
        </div>
        <div class="item-meta">
          <span class="mono">${c.start ?? ""} → ${c.end ?? ""}</span> ·
          ${c.duration_seconds ?? "—"}s · ${c.best_platform ?? ""} · ${c.speaker ?? ""}
        </div>
        ${c.quote_preview ? `<p class="quote">&ldquo;${c.quote_preview}&rdquo;</p>` : ""}
        <p class="item-body">${c.why_clip_this ?? ""}</p>
      </div>`,
    )
    .join("");
  const deadZonesHtml = deadZones
    .map(
      (z) => `<li><span class="mono">${z.start ?? ""} – ${z.end ?? ""}</span> ${pillHtml("gray", String(z.reason ?? ""))}</li>`,
    )
    .join("");
  const peaksHtml = energyPeaks
    .map(
      (p) => `<li><span class="mono">${p.timestamp ?? ""}</span> intensity ${p.intensity ?? "—"} <span class="muted">— ${p.trigger ?? ""}</span></li>`,
    )
    .join("");
  const thumbHtml = thumb && Object.keys(thumb).length > 0
    ? `<div class="item-card">
         <div class="item-meta"><span class="mono">${thumb.timestamp ?? ""}</span> · ${thumb.speaker ?? ""} ${pillHtml("gray", String(thumb.emotion ?? ""))}</div>
         <p class="item-body">${thumb.why ?? ""}</p>
       </div>`
    : "";

  return [
    bestClips.length > 0 ? `<h3>Best Clips</h3>${clipsHtml}` : "",
    deadZones.length > 0 ? `<h3>Dead Zones</h3><ul class="list">${deadZonesHtml}</ul>` : "",
    energyPeaks.length > 0 ? `<h3>Energy Peaks</h3><ul class="list">${peaksHtml}</ul>` : "",
    thumbHtml ? `<h3>Suggested Thumbnail Moment</h3>${thumbHtml}` : "",
  ].join("");
}

function focusQualityHtml(data: Record<string, unknown>): string {
  const scores = data.content_scores as Record<string, unknown> | undefined;
  const strengths = (data.strengths ?? []) as Array<Record<string, unknown>>;
  const improvements = (data.improvements ?? []) as Array<Record<string, unknown>>;
  const pacing = data.pacing_analysis as Record<string, unknown> | undefined;
  const riskZones = (data.retention_risk_zones ?? []) as Array<Record<string, unknown>>;
  const comparison = data.comparison_notes as string | undefined;
  const longest = pacing?.longest_monologue as Record<string, unknown> | undefined;

  const scoresHtml = scores
    ? scoreGridHtml(scores, [
        ["clarity", "Clarity"],
        ["pacing", "Pacing"],
        ["depth", "Depth"],
        ["audience_value", "Audience Value"],
        ["speaker_chemistry", "Chemistry"],
        ["overall_quality", "Overall"],
      ])
    : "";

  const strengthsHtml = strengths
    .map(
      (s) => `
      <div class="item-card border-green">
        <div class="item-meta"><span class="mono">${s.timestamp ?? ""}</span> <strong>${s.aspect ?? ""}</strong></div>
        ${s.example ? `<p class="quote">${s.example}</p>` : ""}
      </div>`,
    )
    .join("");

  const improvementsHtml = improvements
    .map(
      (m) => `
      <div class="item-card border-amber">
        <div class="item-head">
          <span class="mono">${m.timestamp ?? ""}</span> <strong>${m.aspect ?? ""}</strong>
          ${pillHtml(severityTone(String(m.severity ?? "")), String(m.severity ?? ""))}
        </div>
        <p class="item-body">${m.suggestion ?? ""}</p>
      </div>`,
    )
    .join("");

  const pacingHtml = pacing
    ? `<div class="item-card">
         <div class="row"><span class="muted">Verdict</span><strong>${pacing.pacing_verdict ?? "—"}</strong></div>
         <div class="row"><span class="muted">Avg segment</span><span>${pacing.average_segment_seconds ?? "—"}s</span></div>
         <div class="row"><span class="muted">Topic transitions</span><span>${pacing.topic_transitions ?? "—"}</span></div>
         <div class="row"><span class="muted">Dead-air moments</span><span>${pacing.dead_air_moments ?? "—"}</span></div>
         ${longest ? `<div class="row sub"><span class="muted">Longest monologue</span><span>${longest.speaker ?? ""} — ${longest.duration_seconds ?? "—"}s @ <span class="mono">${longest.timestamp ?? ""}</span></span></div>` : ""}
       </div>`
    : "";

  const riskHtml = riskZones
    .map(
      (z) => `<li><span class="mono">${z.start ?? ""} – ${z.end ?? ""}</span> ${pillHtml(riskTone(String(z.risk ?? "")), String(z.risk ?? ""))} <span class="muted">${z.reason ?? ""}</span></li>`,
    )
    .join("");

  const comparisonHtml = comparison
    ? `<p class="item-body">${comparison}</p>`
    : "";

  return [
    scores ? `<h3>Content Scores</h3>${scoresHtml}` : "",
    strengths.length > 0 ? `<h3>Strengths</h3>${strengthsHtml}` : "",
    improvements.length > 0 ? `<h3>Improvements</h3>${improvementsHtml}` : "",
    pacing ? `<h3>Pacing</h3>${pacingHtml}` : "",
    riskZones.length > 0 ? `<h3>Retention Risk Zones</h3><ul class="list">${riskHtml}</ul>` : "",
    comparison ? `<h3>Comparison Notes</h3>${comparisonHtml}` : "",
  ].join("");
}

function focusTeachingHtml(data: Record<string, unknown>): string {
  const scores = data.pedagogy_scores as Record<string, unknown> | undefined;
  const clarity = (data.clarity_moments ?? []) as Array<Record<string, unknown>>;
  const confusion = (data.confusion_signals ?? []) as Array<Record<string, unknown>>;
  const qa = data.qa_effectiveness as Record<string, unknown> | undefined;
  const learningArc = (data.learning_arc ?? []) as Array<Record<string, unknown>>;
  const recs = (data.instructor_recommendations ?? []) as string[];
  const bestQa = qa?.best_qa_moment as Record<string, unknown> | undefined;

  const scoresHtml = scores
    ? scoreGridHtml(scores, [
        ["clarity_of_explanation", "Clarity"],
        ["audience_engagement", "Engagement"],
        ["question_handling", "Q&amp;A"],
        ["structure_and_flow", "Structure"],
        ["knowledge_transfer", "Transfer"],
        ["overall_effectiveness", "Overall"],
      ])
    : "";

  const clarityHtml = clarity
    .map(
      (c) => `
      <div class="item-card border-green">
        <div class="item-head">
          <span class="mono">${c.timestamp ?? ""}</span> ${pillHtml("gray", String(c.type ?? ""))} <strong>${c.topic ?? ""}</strong>
        </div>
        <p class="item-body muted">${c.why_effective ?? ""}</p>
      </div>`,
    )
    .join("");

  const confusionHtml = confusion
    .map(
      (c) => `
      <div class="item-card border-amber">
        <div class="item-head">
          <span class="mono">${c.timestamp ?? ""}</span> ${pillHtml("amber", String(c.type ?? ""))} <strong>${c.topic ?? ""}</strong>
        </div>
        <p class="item-body">${c.suggestion ?? ""}</p>
      </div>`,
    )
    .join("");

  const qaHtml = qa
    ? `<div class="item-card">
         <div class="row"><span class="muted">From audience</span><span>${qa.questions_from_audience ?? "—"}</span></div>
         <div class="row"><span class="muted">From instructor</span><span>${qa.questions_from_instructor ?? "—"}</span></div>
         <div class="row"><span class="muted">Answered clearly</span><span>${qa.answered_clearly ?? "—"}</span></div>
         <div class="row"><span class="muted">Deferred / skipped</span><span>${qa.deferred_or_skipped ?? "—"}</span></div>
         ${bestQa ? `<div class="row sub"><span class="muted">Best Q&amp;A</span><span><span class="mono">${bestQa.timestamp ?? ""}</span> — ${bestQa.question_summary ?? ""} ${pillHtml("gray", String(bestQa.answer_quality ?? ""))}</span></div>` : ""}
       </div>`
    : "";

  const arcHtml = learningArc
    .map(
      (p) => `
      <div class="item-card">
        <div class="item-head">
          ${pillHtml("gray", String(p.phase ?? ""))}
          <span class="mono">${p.time_range ?? ""}</span>
          <span class="muted">engagement ${p.engagement_level ?? "—"}</span>
        </div>
        <div class="chips">${((p.concepts_covered ?? []) as string[]).map((c) => `<span class="chip">${c}</span>`).join("")}</div>
        <p class="item-body muted">${p.assessment ?? ""}</p>
      </div>`,
    )
    .join("");

  const recsHtml = recs
    .map((r) => `<li>${r}</li>`)
    .join("");

  return [
    scores ? `<h3>Pedagogy Scores</h3>${scoresHtml}` : "",
    clarity.length > 0 ? `<h3>Clarity Moments</h3>${clarityHtml}` : "",
    confusion.length > 0 ? `<h3>Confusion Signals</h3>${confusionHtml}` : "",
    qa ? `<h3>Q&amp;A Effectiveness</h3>${qaHtml}` : "",
    learningArc.length > 0 ? `<h3>Learning Arc</h3>${arcHtml}` : "",
    recs.length > 0 ? `<h3>Instructor Recommendations</h3><ul class="bullets">${recsHtml}</ul>` : "",
  ].join("");
}

function focusAnalysisHtml(data: Record<string, unknown>): string {
  const mode = data.mode as string | undefined;
  if (mode === "editing") return focusEditingHtml(data);
  if (mode === "quality") return focusQualityHtml(data);
  if (mode === "teaching") return focusTeachingHtml(data);
  return "";
}

function exportSentimentToPdf(rawContent: unknown, title: string, focusMode?: string | null) {
  let parsed: Record<string, unknown> | null = null;
  try {
    if (typeof rawContent === "string") {
      parsed = JSON.parse(rawContent);
    } else if (rawContent && typeof rawContent === "object") {
      parsed = rawContent as Record<string, unknown>;
    }
  } catch {}
  if (!parsed) return;

  const overall = (parsed.overall ?? {}) as Record<string, unknown>;
  const speakers = (parsed.speakers ?? []) as Array<Record<string, unknown>>;
  const highlights = (parsed.highlights ?? []) as Array<Record<string, unknown>>;
  const summary = ((parsed.summary as string) ?? "");
  const focusAnalysis = (parsed.focus_analysis ?? {}) as Record<string, unknown>;

  const focusBadge = focusMode
    ? `<div class="badge">Optimized for: ${focusMode}</div>`
    : "";

  const overallHtml = scoreGridHtml(overall, [
    ["dominant_tone", "Dominant tone"],
    ["sentiment_score", "Sentiment score"],
    ["engagement_level", "Engagement"],
    ["energy_trend", "Energy trend"],
    ["collaboration_score", "Collaboration"],
    ["tension_count", "Tensions"],
  ]);

  const speakersHtml = speakers
    .map((s) => {
      const sent = (s.sentiment ?? {}) as Record<string, number>;
      const emotions = ((s.dominant_emotions ?? []) as string[])
        .map((e) => `<span class="chip">${e}</span>`)
        .join("");
      const total = (sent.positive ?? 0) + (sent.neutral ?? 0) + (sent.negative ?? 0) || 100;
      const pos = ((sent.positive ?? 0) / total) * 100;
      const neu = ((sent.neutral ?? 0) / total) * 100;
      const neg = ((sent.negative ?? 0) / total) * 100;
      return `
        <div class="speaker">
          <div class="sp-head"><strong>${s.name ?? ""}</strong><span class="muted">${s.style ?? ""}</span></div>
          <div class="bar"><div class="bar-pos" style="width:${pos}%"></div><div class="bar-neu" style="width:${neu}%"></div><div class="bar-neg" style="width:${neg}%"></div></div>
          <div style="display:flex;justify-content:space-between;font-size:9px;color:#6b7280;margin-top:2px;">
            <span style="color:#16a34a">${Math.round(pos)}% pos</span>
            <span>${Math.round(neu)}% neutral</span>
            <span style="color:#ef4444">${Math.round(neg)}% neg</span>
          </div>
          <div class="chips">${emotions}</div>
        </div>`;
    })
    .join("");

  const highlightsHtml = highlights
    .map(
      (h) => `
        <div class="highlight">
          <div class="h-meta"><span class="mono">${h.timestamp ?? ""}</span> &middot; <strong>${h.type ?? ""}</strong> &middot; ${h.speaker ?? ""}</div>
          <p class="quote">&ldquo;${h.quote ?? ""}&rdquo;</p>
          <p class="why">${h.why ?? ""}</p>
        </div>`,
    )
    .join("");

  const focusHtml =
    focusAnalysis && Object.keys(focusAnalysis).length > 1
      ? `<section><h2>Focus analysis</h2>${focusAnalysisHtml(focusAnalysis)}</section>`
      : "";

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
    <title>${title} — Sentiment Analysis</title>
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; margin: 40px; color: #1a1a1a; }
      h1 { font-size: 22px; margin-bottom: 4px; }
      h2 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.06em; color: #6b7280; margin: 24px 0 10px 0; }
      h3 { font-size: 13px; font-weight: 600; color: #374151; margin: 16px 0 8px 0; }
      .subtitle { color: #6b7280; font-size: 12px; margin: 0 0 16px 0; }
      .badge { display: inline-block; background: #eef2ff; color: #4f46e5; padding: 4px 10px; border-radius: 999px; font-size: 11px; font-weight: 600; margin-bottom: 16px; }
      .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
      .metric { background: #f9fafb; padding: 12px; border-radius: 6px; text-align: center; }
      .m-label { font-size: 10px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.04em; }
      .m-value { font-size: 16px; font-weight: 600; margin-top: 4px; }
      .speaker { margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px solid #e5e7eb; }
      .speaker:last-child { border-bottom: none; }
      .sp-head { font-size: 13px; margin-bottom: 6px; }
      .muted { color: #6b7280; font-size: 11px; margin-left: 6px; }
      .bar { display: flex; height: 8px; border-radius: 4px; overflow: hidden; margin-bottom: 6px; }
      .bar-pos { background: #22c55e; }
      .bar-neu { background: #d1d5db; }
      .bar-neg { background: #ef4444; }
      .chips { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
      .chip { background: #f3f4f6; color: #374151; padding: 2px 8px; border-radius: 999px; font-size: 10px; }
      .highlight { border-left: 3px solid #c7d2fe; padding: 6px 10px; margin-bottom: 10px; }
      .h-meta { font-size: 11px; color: #6b7280; margin-bottom: 4px; }
      .mono { font-family: ui-monospace, SFMono-Regular, monospace; }
      .quote { font-style: italic; font-size: 12px; margin: 4px 0; color: #4b5563; }
      .why { font-size: 11px; color: #6b7280; margin: 0; }
      .summary { font-size: 12px; line-height: 1.6; white-space: pre-line; }
      .item-card { border-left: 3px solid #c7d2fe; padding: 8px 12px; margin-bottom: 10px; background: #fafafa; border-radius: 0 6px 6px 0; }
      .item-card.border-green { border-left-color: #22c55e; }
      .item-card.border-amber { border-left-color: #f59e0b; }
      .item-head { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 12px; }
      .item-meta { font-size: 11px; color: #6b7280; margin: 4px 0; }
      .item-body { font-size: 11px; margin: 4px 0 0 0; }
      .pill { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; }
      .list { font-size: 11px; padding-left: 18px; margin: 6px 0; }
      .list li { margin-bottom: 4px; }
      .bullets { font-size: 12px; padding-left: 18px; margin: 6px 0; }
      .bullets li { margin-bottom: 6px; }
      .row { display: flex; justify-content: space-between; font-size: 11px; padding: 3px 0; }
      .row.sub { padding-top: 6px; margin-top: 6px; border-top: 1px solid #e5e7eb; color: #6b7280; }
      @media print { body { margin: 20px; } }
    </style>
  </head><body>
    <h1>${title}</h1>
    <p class="subtitle">Sentiment Analysis Report</p>
    ${focusBadge}
    <section><h2>Overall</h2>${overallHtml}</section>
    ${speakersHtml ? `<section><h2>Speakers</h2>${speakersHtml}</section>` : ""}
    ${highlightsHtml ? `<section><h2>Key moments</h2>${highlightsHtml}</section>` : ""}
    ${focusHtml}
    ${summary ? `<section><h2>Summary</h2><p class="summary">${summary.replace(/\n/g, "<br>")}</p></section>` : ""}
    <script>window.onafterprint=()=>window.close();window.print();<\/script>
  </body></html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
  }
}

const FOCUS_OPTIONS = [
  { value: "editing", label: "Editing & clipping", desc: "Find the best moments to cut" },
  { value: "quality", label: "Content quality review", desc: "Assess engagement and tone" },
  { value: "teaching", label: "Teaching evaluation", desc: "Analyze audience response" },
] as const;

const FOCUS_LABELS: Record<string, string> = {
  editing: "Editing & clipping",
  quality: "Content quality review",
  teaching: "Teaching evaluation",
};

function SentimentQuiz({ jobId }: { jobId: number }) {
  const [selected, setSelected] = useState<string | null>(null);
  const mutation = useGenerateSentiment();

  return (
    <Card>
      <CardContent className="py-8 flex flex-col items-center gap-6 max-w-md mx-auto">
        <div className="text-center space-y-1">
          <h3 className="text-lg font-semibold text-primary">Sentiment Analysis</h3>
          <p className="text-sm text-muted-foreground">
            AI-powered emotional dynamics analysis. One question to optimize the report:
          </p>
        </div>
        <div className="w-full space-y-2">
          <p className="text-sm font-medium">What will you use this analysis for?</p>
          {FOCUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => setSelected(opt.value)}
              className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                selected === opt.value
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/40"
              }`}
            >
              <span className="text-sm font-medium">{opt.label}</span>
              <span className="block text-xs text-muted-foreground mt-0.5">{opt.desc}</span>
            </button>
          ))}
        </div>
        <button
          disabled={!selected || mutation.isPending || mutation.isSuccess}
          onClick={() => selected && mutation.mutate({ jobId, focusMode: selected })}
          className="px-6 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary/90 transition-colors"
        >
          {mutation.isPending || mutation.isSuccess ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Generating...
            </span>
          ) : (
            "Generate Analysis"
          )}
        </button>
      </CardContent>
    </Card>
  );
}

type PillTone = "green" | "amber" | "red" | "gray";

const PILL_CLASSES: Record<PillTone, string> = {
  green: "bg-green-100 text-green-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
  gray: "bg-muted text-muted-foreground",
};

function Pill({ tone, children }: { tone: PillTone; children: React.ReactNode }) {
  return <span className={`px-1.5 py-0.5 text-xs rounded ${PILL_CLASSES[tone]}`}>{children}</span>;
}

function viralityTone(score: number): PillTone {
  if (score >= 80) return "green";
  if (score >= 60) return "amber";
  return "red";
}

function severityTone(s: string): PillTone {
  if (s === "minor") return "gray";
  if (s === "moderate") return "amber";
  return "red";
}

function riskTone(r: string): PillTone {
  if (r === "low") return "gray";
  if (r === "medium") return "amber";
  return "red";
}

function ScoreGrid({ scores, labels }: { scores: Record<string, unknown>; labels: Record<string, string> }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {Object.entries(labels).map(([key, label]) => {
        const raw = scores[key];
        const numVal = typeof raw === "number" ? raw : null;
        const isPercent = numVal !== null && numVal >= 0 && numVal <= 100;
        return (
          <div key={key} className="text-center p-3 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-lg font-semibold mt-1">{String(raw ?? "—")}</p>
            {isPercent && (
              <div className="mt-1.5 h-1.5 rounded-full bg-muted overflow-hidden mx-auto w-3/4">
                <div
                  className="h-full rounded-full bg-primary/60 transition-all"
                  style={{ width: `${numVal}%` }}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FocusEditing({ data }: { data: Record<string, unknown> }) {
  const bestClips = (data.best_clips ?? []) as Array<Record<string, unknown>>;
  const deadZones = (data.dead_zones ?? []) as Array<Record<string, unknown>>;
  const energyPeaks = (data.energy_peaks ?? []) as Array<Record<string, unknown>>;
  const thumbnail = data.suggested_thumbnail_moment as Record<string, unknown> | undefined;

  return (
    <div className="space-y-4">
      {bestClips.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Best Clips</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {bestClips.map((c, i) => (
              <div key={i} className="border-l-2 border-primary/40 pl-3 py-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm">#{String(c.rank ?? i + 1)} · {String(c.title ?? "")}</span>
                  <div className="flex items-center gap-2">
                    <Pill tone={viralityTone(Number(c.virality_score ?? 0))}>
                      {String(c.virality_score ?? "—")}
                    </Pill>
                    <div className="w-16 h-1.5 rounded-full bg-muted overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          Number(c.virality_score ?? 0) >= 80 ? "bg-green-500" :
                          Number(c.virality_score ?? 0) >= 60 ? "bg-amber-500" : "bg-red-400"
                        }`}
                        style={{ width: `${Math.min(100, Number(c.virality_score ?? 0))}%` }}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1 flex-wrap">
                  <span className="font-mono">{String(c.start ?? "")} → {String(c.end ?? "")}</span>
                  <span>· {String(c.duration_seconds ?? "—")}s</span>
                  <span>· {String(c.best_platform ?? "")}</span>
                  <span>· {String(c.speaker ?? "")}</span>
                </div>
                {c.quote_preview ? (
                  <p className="text-xs italic text-muted-foreground mt-1">&ldquo;{String(c.quote_preview)}&rdquo;</p>
                ) : null}
                <p className="text-xs mt-1">{String(c.why_clip_this ?? "")}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {deadZones.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Dead Zones</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {deadZones.map((z, i) => (
              <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-red-50 dark:bg-red-950/20">
                <div className="w-1 h-8 rounded-full bg-red-400 shrink-0" />
                <div>
                  <span className="font-mono text-xs font-medium text-red-600 dark:text-red-400">
                    {String(z.start ?? "")} – {String(z.end ?? "")}
                  </span>
                  <p className="text-xs text-muted-foreground mt-0.5">{String(z.reason ?? "")}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {energyPeaks.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Energy Peaks</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {energyPeaks.map((p, i) => {
              const intensity = Number(p.intensity ?? 0);
              const tone: PillTone = intensity >= 8 ? "green" : intensity >= 5 ? "amber" : "gray";
              return (
                <div key={i} className="flex items-start gap-3">
                  <div className="text-center shrink-0 w-10">
                    <p className="text-lg font-bold" style={{ color: tone === "green" ? "#16a34a" : tone === "amber" ? "#d97706" : "#6b7280" }}>
                      {String(p.intensity ?? "—")}
                    </p>
                    <p className="text-[9px] text-muted-foreground uppercase">energy</p>
                  </div>
                  <div className="flex-1 border-l-2 border-primary/20 pl-3 py-0.5">
                    <span className="font-mono text-xs text-muted-foreground">{String(p.timestamp ?? "")}</span>
                    <p className="text-xs mt-0.5">{String(p.trigger ?? "")}</p>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
      {thumbnail && Object.keys(thumbnail).length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Suggested Thumbnail Moment</CardTitle></CardHeader>
          <CardContent>
            <div className="text-sm">
              <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                <span className="font-mono">{String(thumbnail.timestamp ?? "")}</span>
                <span>· {String(thumbnail.speaker ?? "")}</span>
                <Pill tone="gray">{String(thumbnail.emotion ?? "")}</Pill>
              </div>
              <p className="mt-1">{String(thumbnail.why ?? "")}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FocusQuality({ data }: { data: Record<string, unknown> }) {
  const contentScores = data.content_scores as Record<string, unknown> | undefined;
  const strengths = (data.strengths ?? []) as Array<Record<string, unknown>>;
  const improvements = (data.improvements ?? []) as Array<Record<string, unknown>>;
  const pacing = data.pacing_analysis as Record<string, unknown> | undefined;
  const riskZones = (data.retention_risk_zones ?? []) as Array<Record<string, unknown>>;
  const comparison = data.comparison_notes as string | undefined;
  const longest = pacing?.longest_monologue as Record<string, unknown> | undefined;

  return (
    <div className="space-y-4">
      {contentScores && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Content Scores</CardTitle></CardHeader>
          <CardContent>
            <ScoreGrid
              scores={contentScores}
              labels={{
                clarity: "Clarity",
                pacing: "Pacing",
                depth: "Depth",
                audience_value: "Audience Value",
                speaker_chemistry: "Chemistry",
                overall_quality: "Overall",
              }}
            />
          </CardContent>
        </Card>
      )}
      {strengths.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Strengths</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {strengths.map((s, i) => (
              <div key={i} className="border-l-2 border-green-400 pl-3 py-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground">{String(s.timestamp ?? "")}</span>
                  <span className="text-sm font-medium">{String(s.aspect ?? "")}</span>
                </div>
                {s.example ? <p className="text-xs italic text-muted-foreground mt-1">{String(s.example)}</p> : null}
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {improvements.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Improvements</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {improvements.map((m, i) => (
              <div key={i} className="border-l-2 border-amber-400 pl-3 py-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{String(m.timestamp ?? "")}</span>
                  <span className="text-sm font-medium">{String(m.aspect ?? "")}</span>
                  <Pill tone={severityTone(String(m.severity ?? ""))}>{String(m.severity ?? "")}</Pill>
                </div>
                <p className="text-xs mt-1">{String(m.suggestion ?? "")}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {pacing && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Pacing</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">Verdict</span><span className="font-medium">{String(pacing.pacing_verdict ?? "—")}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Avg segment</span><span>{String(pacing.average_segment_seconds ?? "—")}s</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Topic transitions</span><span>{String(pacing.topic_transitions ?? "—")}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Dead-air moments</span><span>{String(pacing.dead_air_moments ?? "—")}</span></div>
            {longest && (
              <div className="flex justify-between text-xs text-muted-foreground pt-2 border-t mt-2">
                <span>Longest monologue</span>
                <span>
                  {String(longest.speaker ?? "")} — {String(longest.duration_seconds ?? "—")}s @{" "}
                  <span className="font-mono">{String(longest.timestamp ?? "")}</span>
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {riskZones.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Retention Risk Zones</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {riskZones.map((z, i) => (
                <li key={i} className="flex items-start gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{String(z.start ?? "")} – {String(z.end ?? "")}</span>
                  <Pill tone={riskTone(String(z.risk ?? ""))}>{String(z.risk ?? "")}</Pill>
                  <span className="text-xs text-muted-foreground">{String(z.reason ?? "")}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      {comparison && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Comparison Notes</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-foreground/80 leading-relaxed">{comparison}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FocusTeaching({ data }: { data: Record<string, unknown> }) {
  const pedagogyScores = data.pedagogy_scores as Record<string, unknown> | undefined;
  const clarity = (data.clarity_moments ?? []) as Array<Record<string, unknown>>;
  const confusion = (data.confusion_signals ?? []) as Array<Record<string, unknown>>;
  const qa = data.qa_effectiveness as Record<string, unknown> | undefined;
  const learningArc = (data.learning_arc ?? []) as Array<Record<string, unknown>>;
  const recommendations = (data.instructor_recommendations ?? []) as string[];
  const bestQa = qa?.best_qa_moment as Record<string, unknown> | undefined;

  return (
    <div className="space-y-4">
      {pedagogyScores && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Pedagogy Scores</CardTitle></CardHeader>
          <CardContent>
            <ScoreGrid
              scores={pedagogyScores}
              labels={{
                clarity_of_explanation: "Clarity",
                audience_engagement: "Engagement",
                question_handling: "Q&A",
                structure_and_flow: "Structure",
                knowledge_transfer: "Transfer",
                overall_effectiveness: "Overall",
              }}
            />
          </CardContent>
        </Card>
      )}
      {clarity.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Clarity Moments</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {clarity.map((c, i) => (
              <div key={i} className="border-l-2 border-green-400 pl-3 py-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{String(c.timestamp ?? "")}</span>
                  <Pill tone="gray">{String(c.type ?? "")}</Pill>
                  <span className="text-sm">{String(c.topic ?? "")}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{String(c.why_effective ?? "")}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {confusion.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Confusion Signals</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {confusion.map((c, i) => (
              <div key={i} className="border-l-2 border-amber-400 pl-3 py-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-xs text-muted-foreground">{String(c.timestamp ?? "")}</span>
                  <Pill tone="amber">{String(c.type ?? "")}</Pill>
                  <span className="text-sm">{String(c.topic ?? "")}</span>
                </div>
                <p className="text-xs mt-1">{String(c.suggestion ?? "")}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {qa && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Q&amp;A Effectiveness</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <div className="flex justify-between"><span className="text-muted-foreground">From audience</span><span>{String(qa.questions_from_audience ?? "—")}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">From instructor</span><span>{String(qa.questions_from_instructor ?? "—")}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Answered clearly</span><span>{String(qa.answered_clearly ?? "—")}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Deferred / skipped</span><span>{String(qa.deferred_or_skipped ?? "—")}</span></div>
            {bestQa && (
              <div className="text-xs text-muted-foreground pt-2 border-t mt-2 flex flex-wrap items-center gap-1">
                <span>Best Q&amp;A:</span>
                <span className="font-mono">{String(bestQa.timestamp ?? "")}</span>
                <span>— {String(bestQa.question_summary ?? "")}</span>
                <Pill tone="gray">{String(bestQa.answer_quality ?? "")}</Pill>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {learningArc.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Learning Arc</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {learningArc.map((p, i) => (
              <div key={i} className="border-l-2 border-primary/40 pl-3 py-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill tone="gray">{String(p.phase ?? "")}</Pill>
                  <span className="font-mono text-xs text-muted-foreground">{String(p.time_range ?? "")}</span>
                  <span className="text-xs text-muted-foreground">engagement {String(p.engagement_level ?? "—")}</span>
                </div>
                <div className="flex flex-wrap gap-1 mt-1">
                  {((p.concepts_covered ?? []) as string[]).map((c) => (
                    <span key={c} className="px-2 py-0.5 text-xs rounded-full bg-muted">{c}</span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{String(p.assessment ?? "")}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {recommendations.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Instructor Recommendations</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-1.5 text-sm">
              {recommendations.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function FocusAnalysisSection({ data }: { data: Record<string, unknown> }) {
  const mode = data.mode as string | undefined;
  if (mode === "editing") return <FocusEditing data={data} />;
  if (mode === "quality") return <FocusQuality data={data} />;
  if (mode === "teaching") return <FocusTeaching data={data} />;
  return null;
}

function SentimentReport({ content, focusMode }: { content: unknown; focusMode?: string | null }) {
  let parsed: Record<string, unknown> | null = null;
  try {
    if (typeof content === "string") {
      parsed = JSON.parse(content);
    } else if (content && typeof content === "object") {
      parsed = content as Record<string, unknown>;
    }
  } catch {}

  if (!parsed) {
    const fallback = typeof content === "string" ? content : JSON.stringify(content, null, 2);
    return (
      <Card>
        <CardContent className="py-8">
          <pre className="text-xs whitespace-pre-wrap text-muted-foreground">{fallback}</pre>
        </CardContent>
      </Card>
    );
  }

  const overall = parsed.overall as Record<string, unknown> | undefined;
  const speakers = parsed.speakers as Array<Record<string, unknown>> | undefined;
  const highlights = parsed.highlights as Array<Record<string, unknown>> | undefined;
  const summary = parsed.summary as string | undefined;
  const focusAnalysis = parsed.focus_analysis as Record<string, unknown> | undefined;

  return (
    <div className="space-y-4">
      {focusMode && (
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
          <span>&#10022;</span> Optimized for: {FOCUS_LABELS[focusMode] ?? focusMode}
        </div>
      )}

      {overall && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Overall Sentiment</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                ["Tone", overall.dominant_tone],
                ["Score", overall.sentiment_score],
                ["Engagement", overall.engagement_level],
                ["Energy", overall.energy_trend],
                ["Collaboration", overall.collaboration_score],
                ["Tensions", overall.tension_count],
              ].map(([label, value]) => (
                <div key={String(label)} className="text-center p-3 rounded-lg bg-muted/50">
                  <p className="text-xs text-muted-foreground">{String(label)}</p>
                  <p className="text-lg font-semibold mt-1">{String(value ?? "-")}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {speakers && speakers.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Speaker Sentiment</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {speakers.map((s, i) => {
              const sent = s.sentiment as Record<string, number> | undefined;
              return (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{String(s.name)}</span>
                    <span className="text-xs text-muted-foreground">{String(s.style)}</span>
                  </div>
                  {sent && (
                    <>
                      <div className="flex h-2.5 rounded-full overflow-hidden">
                        <div className="bg-green-500" style={{ width: `${sent.positive}%` }} />
                        <div className="bg-gray-300" style={{ width: `${sent.neutral}%` }} />
                        <div className="bg-red-400" style={{ width: `${sent.negative}%` }} />
                      </div>
                      <div className="flex justify-between text-[10px] text-muted-foreground mt-0.5">
                        <span className="text-green-600">{sent.positive}% pos</span>
                        <span>{sent.neutral}% neutral</span>
                        <span className="text-red-500">{sent.negative}% neg</span>
                      </div>
                    </>
                  )}
                  <div className="flex gap-1 flex-wrap">
                    {(s.dominant_emotions as string[] || []).map((e: string) => (
                      <span key={e} className="px-2 py-0.5 text-xs rounded-full bg-muted">{e}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {highlights && highlights.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Key Moments</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {highlights.map((h, i) => (
              <div key={i} className="border-l-2 border-primary/40 pl-3 py-1">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="font-mono">{String(h.timestamp)}</span>
                  <span className="px-1.5 py-0.5 rounded bg-muted">{String(h.type)}</span>
                  <span>{String(h.speaker)}</span>
                </div>
                <p className="text-sm mt-1 italic">&ldquo;{String(h.quote)}&rdquo;</p>
                <p className="text-xs text-muted-foreground mt-0.5">{String(h.why)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {focusAnalysis && Object.keys(focusAnalysis).length > 1 && (
        <FocusAnalysisSection data={focusAnalysis} />
      )}

      {summary && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{summary}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// A8 — per-tab "updated since last viewed" tracking, persisted in localStorage
// under "eduarta:viewed:{jobId}" as { reportType: ISO timestamp last viewed }.
function useTabViewed(jobId: string | undefined) {
  const storageKey = jobId ? `eduarta:viewed:${jobId}` : null;

  const [viewed, setViewed] = useState<Record<string, string>>({});

  // Load (and reset on job switch) from localStorage.
  useEffect(() => {
    if (!storageKey) {
      setViewed({});
      return;
    }
    try {
      setViewed(JSON.parse(localStorage.getItem(storageKey) || "{}"));
    } catch {
      setViewed({});
    }
  }, [storageKey]);

  const markViewed = useCallback(
    (reportType: string, reportUpdatedAt?: string) => {
      if (!storageKey) return;
      setViewed((prev) => {
        // Store the report's own server timestamp (not wall-clock) so the later
        // isUpdated() comparison is server-time vs server-time — no clock skew.
        const next = { ...prev, [reportType]: reportUpdatedAt || new Date().toISOString() };
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          /* localStorage unavailable (private mode / quota) — badges degrade silently */
        }
        return next;
      });
    },
    [storageKey],
  );

  // true only when the report changed after the user last viewed that tab.
  // No stored timestamp ⇒ first visit ⇒ no dot.
  const isUpdated = useCallback(
    (reportType: string, reportUpdatedAt?: string | null) => {
      if (!reportUpdatedAt) return false;
      const seen = viewed[reportType];
      if (!seen) return false;
      return new Date(reportUpdatedAt).getTime() > new Date(seen).getTime();
    },
    [viewed],
  );

  return { markViewed, isUpdated };
}

export function ProjectView({ position }: ProjectViewProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: jobs, isLoading: isLoadingJobs } = useJobs();

  const job = jobs?.find((j) => j.position === position);
  // The route carries a project's position in the list, not its id, so deleting a project
  // leaves whoever was looking at it on a URL that resolves to nothing. Distinguish that from
  // "the list has not arrived yet", where job is also undefined.
  const jobNotFound = !isLoadingJobs && jobs !== undefined && job === undefined;
  const jobId = job?.id?.toString();
  const isJobActive = job?.status === "PENDING" || job?.status === "PROCESSING";

  const personaTabs = getPersonaTabs(job?.persona, job?.report_labels);
  const tabLabel = (type: PromptReportType): string =>
    personaTabs.find((t) => t.type === type)?.label ?? PROMPT_REPORT_LABELS[type];

  // undefined = "follow the job's saved output language"; a string overrides it (?language=).
  // Declared before usePromptReports/usePromptReport since both read it.
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(undefined);

  const { data: report, isLoading: isLoadingReport } = useReport(String(position), isJobActive);
  const { data: promptReports } = usePromptReports(jobId, job?.status, selectedLanguage);
  const { data: jobInfo } = useJobInfo(job?.id ?? null);
  const [activeTab, setActiveTab] = useState<PromptReportType>("SUMMARY");
  const { markViewed, isUpdated } = useTabViewed(jobId);
  const [refineOpen, setRefineOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const regenerate = useRegenerateReport();
  const renameJob = useRenameJob();

  // FIX-9b: one-time backfill of pre-FIX-9 renames. If the user renamed this
  // project locally (before titles were persisted) but the backend still has no
  // custom_title, silently push the local title to the server — once per job,
  // no UI.
  const syncedTitleIds = useRef<Set<number>>(new Set());
  useEffect(() => {
    if (!job) return;
    const meta = getProjectMeta(job.id);
    const localTitle = meta?.titleEditedByUser ? meta.customTitle?.trim() : "";
    if (localTitle && !job.custom_title?.trim() && !syncedTitleIds.current.has(job.id)) {
      syncedTitleIds.current.add(job.id);
      renameJob.mutate({ jobId: job.id, title: localTitle });
    }
  }, [job?.id, job?.custom_title]);

  // PersonaStrip: switch between cached persona/language variants without regenerating.
  const { data: variantData } = useAvailableVariants(jobId);
  const variants = variantData?.variants;
  const addable = variantData?.addable;
  const { data: refineContext } = useRefineContext(jobId);
  const setPersona = useSetPersona();
  const currentLanguage = selectedLanguage ?? (refineContext?.output_language ?? "");
  // Source/original language code for tab labels ("Transcript (EN)") and the notice below.
  const sourceLangCode = (job?.detected_language || jobInfo?.language || "").toUpperCase();
  // Persona reports are being viewed in a language other than the source — flag that
  // transcript/sentiment stay in the original language.
  const showSourceLangNotice =
    currentLanguage !== "" && !!sourceLangCode && currentLanguage.toUpperCase() !== sourceLangCode;

  const handlePersonaChange = (persona: Persona) => {
    if (!job || persona === job.persona) return;
    const variant = variants?.find((v) => v.persona === persona);
    const langs = variant?.languages ?? [""];
    // Keep the current language if the target persona has it cached; otherwise its first.
    setSelectedLanguage(langs.includes(currentLanguage) ? currentLanguage : (langs[0] ?? ""));
    setHasAutoSwitched(false);
    // Send the variant's canonical role so role + scenario follow the persona; without it
    // job.role keeps pointing at whatever role was chosen last and a later retry/refine
    // would build this persona's reports from the wrong system prompt.
    if (variant?.role_slug) {
      setPersona.mutate({ jobId: job.id, roleSlug: variant.role_slug });
    } else {
      setPersona.mutate({ jobId: job.id, persona });
    }
  };

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language);
    setHasAutoSwitched(false);
  };

  // If the active tab isn't part of the persona's tab list (e.g., persona just loaded
  // or differs from default), snap to the persona's first tab.
  useEffect(() => {
    if (!job?.persona) return;
    if (!personaTabs.some((t) => t.type === activeTab) && activeTab !== ("SENTIMENT" as PromptReportType) && personaTabs[0]) {
      setActiveTab(personaTabs[0].type);
    }
  }, [job?.persona, activeTab, personaTabs]);

  // Mark the active tab viewed on mount and whenever it changes (covers clicks
  const activePR = promptReports?.find((r) => r.report_type === activeTab);

  // Mark the active tab viewed on mount and whenever it (or its report) changes —
  // covers clicks and programmatic auto-switches — clearing its updated-dot.
  // Stores the report's server updated_at so the comparison stays skew-free.
  useEffect(() => {
    if (activeTab) markViewed(activeTab, activePR?.updated_at);
  }, [activeTab, activePR?.updated_at, markViewed]);

  const isActivePRPending = !activePR || activePR.status === "CREATED" || activePR.status === "PROCESSING";
  const isActivePRFailed = activePR?.status === "FAILED";
  // "Taking longer than expected" once a report has been PROCESSING for > 5 min.
  // updated_at marks when processing started (status flip), so re-running an older
  // report doesn't instantly look stuck.
  const FIVE_MINUTES_MS = 5 * 60 * 1000;
  const isActivePRSlow =
    activePR?.status === "PROCESSING" &&
    !!activePR.updated_at &&
    Date.now() - new Date(activePR.updated_at).getTime() > FIVE_MINUTES_MS;

  // For on-demand SENTIMENT: skip the detail fetch entirely if no row exists yet,
  // so we don't poll a 404 endpoint while the quiz is showing.
  const detailReportType: PromptReportType | null =
    activeTab === ("SENTIMENT" as PromptReportType) && !activePR ? null : activeTab;

  const { data: promptReportDetail, isLoading: isLoadingPromptReport } = usePromptReport(
    jobId,
    detailReportType,
    isActivePRPending,
    selectedLanguage,
  );

  // "The Gist" — a plain-language brief that cascades from the Summary. The list carries
  // its row, so whether one exists is a fact rather than something to discover by asking
  // the detail endpoint and seeing whether it 404s. Fetch the text only once the row says
  // it is there; a job whose Gist is still queued simply has no card yet.
  const gistRow = promptReports?.find((r) => r.report_type === "PLAIN_BRIEF");
  const wantGist = activeTab === "SUMMARY" && gistRow?.status === "PROCESSED";
  const { data: gistDetail } = usePromptReport(
    jobId,
    wantGist ? ("PLAIN_BRIEF" as PromptReportType) : null,
    wantGist,
    selectedLanguage,
    // The Gist is excluded from the report list, so nothing here can tell a 404 that is
    // about to resolve from one that never will — this is the call site the give-up
    // cap exists for.
    true,
  );
  const gistText =
    gistDetail?.status === "PROCESSED" && gistDetail.content.trim() ? gistDetail.content.trim() : null;

  // When the job transitions out of active state (e.g., COMPLETED), force-refetch
  // prompt-reports so the list appears even if backend created rows just then.
  const prevJobStatusRef = useRef(job?.status);
  useEffect(() => {
    if (prevJobStatusRef.current !== job?.status && job?.status === "COMPLETED" && jobId) {
      queryClient.invalidateQueries({ queryKey: ["prompt-reports", jobId] });
      queryClient.invalidateQueries({ queryKey: ["reports", String(position)] });
      // Variants are populated as reports finish; refresh so the PersonaStrip and
      // the header persona/role badge reflect what's actually cached on completion.
      queryClient.invalidateQueries({ queryKey: ["available-variants", jobId] });
    }
    prevJobStatusRef.current = job?.status;
  }, [job?.status, jobId, position, queryClient]);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [isDownloadingZip, setIsDownloadingZip] = useState(false);
  const [docxLoadingId, setDocxLoadingId] = useState<number | null>(null);
  const [subtitleLoadingFmt, setSubtitleLoadingFmt] = useState<DownloadFormat | null>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);

  const projectName = job ? getProjectName(job) : `Report ${position}`;
  const dateStr = job?.created_at ? formatDate(job.created_at) : "";
  const safeName = projectName.replace(/[^a-zA-Z0-9 ]/g, "").trim().replace(/\s+/g, "_");

  // Generation progress — the tab grid only, so a pending Gist never makes the project
  // look unfinished.
  const gridPromptReports = gridReports(promptReports);
  const totalReports = gridPromptReports.length;
  const readyReports = gridPromptReports.filter((r) => r.status === "PROCESSED").length;
  const failedReports = gridPromptReports.filter((r) => r.status === "FAILED").length;
  const isGenerating = totalReports > 0 && readyReports + failedReports < totalReports;
  const overallProgress = totalReports > 0 ? Math.round(((readyReports + failedReports) / totalReports) * 100) : 0;
  const currentlyGenerating =
    gridPromptReports.find((r) => r.status === "PROCESSING") ?? gridPromptReports.find((r) => r.status === "CREATED");

  // Auto-switch active tab to first ready report once one becomes available,
  // so the user sees content as soon as it's there.
  const [hasAutoSwitched, setHasAutoSwitched] = useState(false);
  useEffect(() => {
    if (hasAutoSwitched || !promptReports) return;
    const activePr = promptReports.find((r) => r.report_type === activeTab);
    if (activePr?.status === "PROCESSED") {
      setHasAutoSwitched(true);
      return;
    }
    const firstReady = promptReports.find((r) => r.status === "PROCESSED" && r.report_type !== "SENTIMENT");
    if (firstReady) {
      setActiveTab(firstReady.report_type);
      setHasAutoSwitched(true);
    }
  }, [promptReports, activeTab, hasAutoSwitched]);

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Close download menu on outside click
  useEffect(() => {
    if (!showDownloadMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showDownloadMenu]);

  const startEditTitle = () => {
    setEditTitle(projectName);
    setIsEditingTitle(true);
  };

  const saveTitle = () => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== projectName && job) {
      // Local meta gives an instant optimistic update; the backend rename
      // persists it so the DOCX/PDF filenames pick up the new title too.
      setProjectMeta(job.id, { customTitle: trimmed, titleEditedByUser: true });
      renameJob.mutate({ jobId: job.id, title: trimmed });
    }
    setIsEditingTitle(false);
  };

  const cancelEditTitle = () => {
    setIsEditingTitle(false);
  };

  // Persona-aware PDF metadata.
  const personaLabel = job?.persona
    ? job.persona.charAt(0).toUpperCase() + job.persona.slice(1)
    : undefined;
  // The persona currently shown in the strip (falls back to "business" when the
  // job has none). When more than one variant exists the user can switch persona,
  // so the static role/scenario badge no longer applies and we show the persona
  // name instead.
  const currentPersona: Persona = (job?.persona as Persona) || "business";
  const nonTranscriptTabs = personaTabs.filter((t) => t.type !== "TRANSCRIPT");
  const reportTotal = nonTranscriptTabs.length || undefined;
  const reportNumberFor = (type: PromptReportType): number | undefined => {
    const idx = nonTranscriptTabs.findIndex((t) => t.type === type);
    return idx >= 0 ? idx + 1 : undefined;
  };

  const handleSingleDownload = (type: PromptReportType, format: DownloadFormat) => {
    if (!promptReportDetail?.content && type === activeTab) return;
    // For active tab we have content already; for others we'd need to fetch.
    // Since the dropdown shows per-row formats, and we need content for each,
    // we fetch on demand via the API.
    if (type === activeTab && promptReportDetail?.content) {
      const label = tabLabel(type);
      const title = `${projectName} — ${label}`;
      const fileBase = `${safeName}_${label.replace(/\s+/g, "_")}`;
      downloadReport(promptReportDetail.content, title, fileBase, format, {
        projectTitle: projectName,
        reportType: type,
        reportLabel: label,
        personaLabel,
        reportNumber: reportNumberFor(type),
        reportTotal,
        gist: type === "SUMMARY" ? (gistText ?? undefined) : undefined,
      });
    }
    setShowDownloadMenu(false);
  };

  // DOCX is rendered server-side from the report's markdown, so it works for any
  // PROCESSED report by id — no need for the report to be the active tab.
  const handleDocxDownload = async (reportId: number) => {
    if (docxLoadingId !== null) return;
    setDocxLoadingId(reportId);
    try {
      await downloadReportDocx(reportId);
    } catch (err) {
      console.error("DOCX download failed:", err);
      alert("Something went wrong. Please try again or contact support.");
    } finally {
      setDocxLoadingId(null);
      setShowDownloadMenu(false);
    }
  };

  // Subtitles (TRANSCRIPT row) are generated server-side from transcript elements,
  // so they download for any PROCESSED transcript regardless of the active tab —
  // no loaded content required (unlike PDF/DOCX).
  const handleSubtitleDownload = async (format: DownloadFormat) => {
    if (!jobId || subtitleLoadingFmt !== null) return;
    setSubtitleLoadingFmt(format);
    try {
      await downloadJobSubtitles(jobId, format);
    } catch (err) {
      console.error("Subtitle download failed:", err);
      alert("Something went wrong. Please try again or contact support.");
    } finally {
      setSubtitleLoadingFmt(null);
      setShowDownloadMenu(false);
    }
  };

  const handleDownloadAll = async () => {
    const reportsToDownload = (promptReports ?? []).filter(
      (r) => r.status === "PROCESSED",
    );
    if (!jobId || reportsToDownload.length === 0) return;
    setIsDownloadingZip(true);
    try {
      const token = tokenStorage.getAccessToken();

      const fetches = reportsToDownload.map(async (r) => {
        const res = await fetch(`${API_BASE_URL}/jobs/${jobId}/reports/${r.report_type}/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return null;
        const data = await res.json();
        return {
          type: r.report_type,
          label: tabLabel(r.report_type),
          content: data.content as string,
        } satisfies ReportForDownload;
      });

      const results = (await Promise.all(fetches)).filter(
        (r): r is ReportForDownload => r !== null && !!r.content,
      );

      if (results.length > 0) {
        await downloadAllAsZip(results, projectName, { personaLabel });
      }
    } catch (err) {
      console.error("Download all failed:", err);
      alert("Something went wrong. Please try again or contact support.");
    } finally {
      setIsDownloadingZip(false);
      setShowDownloadMenu(false);
    }
  };

  // Deleted, or never existed. Rendering the normal shell here produced a title of
  // "Report <position>", tabs for a persona nobody selected, and spinners that could never
  // resolve, because every query below hangs off a job that is not there.
  if (jobNotFound) {
    return (
      <div className="flex items-center justify-center h-full p-6" data-qa="project-not-found">
        <div className="max-w-md text-center space-y-4">
          <h1 className="text-2xl font-semibold tracking-tight">This project is no longer here</h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            It was deleted, or the link points at a project that never existed. Your other projects are
            unaffected.
          </p>
          <Button data-qa="project-not-found-back" onClick={() => navigate("/all")}>
            Go to all projects
          </Button>
        </div>
      </div>
    );
  }

  // If still loading everything
  if (isLoadingReport && !promptReports) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full" data-qa="project-view">
      <div className="flex flex-col h-full flex-1 min-w-0">
      {/* Top bar */}
      <div className="px-6 py-4 border-b flex items-center gap-3 shrink-0">
        {isEditingTitle ? (
          <input
            ref={titleInputRef}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveTitle();
              if (e.key === "Escape") cancelEditTitle();
            }}
            className="text-base font-medium flex-1 bg-transparent border-b border-dashed border-muted-foreground/30 outline-none py-0.5"
          />
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <button
              onClick={startEditTitle}
              className="text-base font-medium truncate text-left group flex items-center gap-1.5 min-w-0"
            >
              <span className="truncate">{projectName}</span>
              <Pencil className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </button>
            {(variants?.length ?? 0) > 1 ? (
              <span className="text-sm text-muted-foreground truncate shrink-0">
                · {(() => { const _v = variants?.find((v) => v.persona === currentPersona); const _n = _v?.role_name ?? currentPersona.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()); return _v?.vertical ? _v.vertical + " \u00b7 " + _n : _n; })()}
              </span>
            ) : (
              (() => {
                const parts = [job?.role_name, job?.scenario_name].filter(Boolean) as string[];
                return parts.length > 0 ? (
                  <span className="text-sm text-muted-foreground truncate shrink-0">
                    · {parts.join(" · ")}
                  </span>
                ) : null;
              })()
            )}
          </div>
        )}
        {dateStr && <span className="text-sm text-muted-foreground shrink-0">{dateStr}</span>}
        {job?.detected_language && (
          <span
            className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-muted text-muted-foreground shrink-0 uppercase tracking-wide"
            title="Detected source language"
          >
            {job.detected_language}
          </span>
        )}

        {/* AI Dialogue chat */}
        {job?.status === "COMPLETED" && (
          <Button
            variant={chatOpen ? "default" : "outline"}
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={() => {
              setChatOpen((v) => !v);
              setRefineOpen(false);
              setMapOpen(false);
            }}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            Ask AI
          </Button>
        )}

        {/* Refine & regenerate */}
        {job?.status === "COMPLETED" && (
          <Button
            variant={refineOpen ? "default" : "outline"}
            size="sm"
            className="gap-1.5 shrink-0"
            data-qa="refine-toggle"
            onClick={() => {
              setRefineOpen((v) => !v);
              setChatOpen(false);
              setMapOpen(false);
            }}
          >
            <SlidersHorizontal className="w-3.5 h-3.5" />
            Refine
          </Button>
        )}

        {/* Project map (version tree) */}
        {job?.status === "COMPLETED" && (
          <Button
            variant={mapOpen ? "default" : "outline"}
            size="sm"
            className="gap-1.5 shrink-0"
            onClick={() => {
              setMapOpen((v) => !v);
              setChatOpen(false);
              setRefineOpen(false);
            }}
          >
            <Network className="w-3.5 h-3.5" />
            Map
          </Button>
        )}

        {/* Download dropdown */}
        <div className="relative shrink-0" ref={downloadMenuRef}>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setShowDownloadMenu((v) => !v)}
          >
            <Download className="w-3.5 h-3.5" />
            Download
          </Button>
          {showDownloadMenu && (
            <div className="absolute right-0 top-full mt-1 w-72 rounded-lg border bg-popover p-1 shadow-md z-50">
              {/* Download all ZIP */}
              <button
                onClick={handleDownloadAll}
                className="w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center gap-2 hover:bg-muted text-foreground cursor-pointer"
              >
                {isDownloadingZip ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
                Download all (ZIP)
              </button>

              <div className="border-t my-1" />

              {/* Per-report rows */}
              {personaTabs.map(({ type, label }) => {
                const pr = promptReports?.find((r) => r.report_type === type);
                if (!pr) return null;
                const isReady = pr.status === "PROCESSED";
                const formats = getFormatsForType(type);

                return (
                  <div
                    key={type}
                    className="flex items-center justify-between px-3 py-1.5 text-sm rounded-md"
                  >
                    <span className={isReady ? "text-foreground" : "text-muted-foreground/50"}>
                      {label}
                    </span>
                    <div className="flex items-center gap-1">
                      {formats.map(({ format, label: fmtLabel, enabled }) => {
                        const isDocx = format === "docx";
                        const isTranscript = type === "TRANSCRIPT";
                        // PDF/DOCX render client-side from the loaded content, so they're
                        // only downloadable for the report on the active tab. Subtitles
                        // (TRANSCRIPT) are generated server-side from transcript elements,
                        // so they download regardless of which tab is loaded.
                        const hasContent = type === activeTab && !!promptReportDetail?.content;
                        const canDownload = isReady && enabled && (isTranscript || hasContent);
                        const isDocxLoading = isDocx && docxLoadingId === pr.id;
                        const isSubtitleLoading = isTranscript && subtitleLoadingFmt === format;
                        return (
                          <button
                            key={format}
                            disabled={!canDownload || isDocxLoading || isSubtitleLoading}
                            onClick={() => {
                              if (!canDownload) return;
                              if (isTranscript) handleSubtitleDownload(format);
                              else if (isDocx) handleDocxDownload(pr.id);
                              else handleSingleDownload(type, format);
                            }}
                            className={`px-2 py-0.5 text-xs rounded transition-colors ${
                              canDownload
                                ? "bg-muted hover:bg-muted/80 text-foreground"
                                : !enabled
                                  ? "text-muted-foreground/40 cursor-not-allowed"
                                  : "bg-muted/50 text-muted-foreground/60 cursor-not-allowed"
                            }`}
                            title={
                              !enabled
                                ? "Coming soon"
                                : !isReady
                                  ? "Report is processing"
                                  : !isTranscript && !hasContent
                                    ? `Switch to ${label} tab first`
                                    : fmtLabel
                            }
                          >
                            {isDocxLoading || isSubtitleLoading ? "…" : fmtLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              {/* Sentiment Analysis row — only shown when SENTIMENT report exists and is ready */}
              {(() => {
                const sentimentPR = promptReports?.find((r) => r.report_type === "SENTIMENT");
                if (!sentimentPR || sentimentPR.status !== "PROCESSED") return null;
                const isCurrent = activeTab === ("SENTIMENT" as PromptReportType);
                const hasContent = isCurrent && !!promptReportDetail?.content;
                return (
                  <div className="flex items-center justify-between px-3 py-1.5 text-sm rounded-md">
                    <span className="text-foreground">Sentiment Analysis</span>
                    <div className="flex items-center gap-1">
                      <button
                        disabled={!hasContent}
                        onClick={() => {
                          if (!hasContent || !promptReportDetail) return;
                          exportSentimentToPdf(
                            promptReportDetail.content,
                            `${projectName} — Sentiment Analysis`,
                            sentimentPR.focus_mode,
                          );
                          setShowDownloadMenu(false);
                        }}
                        className={`px-2 py-0.5 text-xs rounded transition-colors ${
                          hasContent
                            ? "bg-muted hover:bg-muted/80 text-foreground"
                            : "bg-muted/50 text-muted-foreground/60 cursor-not-allowed"
                        }`}
                        title={!hasContent ? "Switch to Sentiment tab first" : "Download as PDF"}
                      >
                        PDF
                      </button>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* File Intelligence strip — source-file metadata (auto-hides when none); opens the info modal */}
      <FileMetadataStrip fm={jobInfo?.file_metadata} onClick={() => setInfoOpen(true)} />

      {/* Persona / language variant switcher — always shown so the user sees the active role */}
      {job?.status === "COMPLETED" && variantData && (
        <PersonaStrip
          jobId={job.id}
          currentPersona={currentPersona}
          currentLanguage={currentLanguage}
          variants={variants ?? []}
          addable={addable ?? []}
          sourceLanguage={job?.detected_language ?? jobInfo?.language ?? ""}
          onPersonaChange={handlePersonaChange}
          onLanguageChange={handleLanguageChange}
        />
      )}

      {/* Tabs (always visible — show all expected report types with loading state) */}
      <div className="px-6 pt-3 flex flex-wrap gap-1 shrink-0" data-qa="report-tabs">
        {personaTabs.map(({ type, label }) => {
          const pr = promptReports?.find((r) => r.report_type === type);
          const isActive = activeTab === type;
          const isReady = pr?.status === "PROCESSED";
          const isFailed = pr?.status === "FAILED";
          const isPending = !isReady && !isFailed;
          const showDot = !isActive && isUpdated(type, pr?.updated_at ?? job?.created_at);

          return (
            <button
              key={type}
              data-qa={`report-tab-${type}`}
              onClick={() => {
                markViewed(type, pr?.updated_at);
                setActiveTab(type);
              }}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors inline-flex items-center ${
                isActive
                  ? "bg-foreground text-background font-medium"
                  : isReady
                    ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                    : "text-muted-foreground/50"
              }`}
            >
              {isPending && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}
              {label}
              {type === "TRANSCRIPT" && sourceLangCode ? ` (${sourceLangCode})` : ""}
              {showDot && <span className="w-2 h-2 rounded-full bg-green-500 ml-1.5 shrink-0" />}
            </button>
          );
        })}

        {/* Sentiment tab — on-demand, separate from persona tabs */}
        {job?.status === "COMPLETED" && (() => {
          const sentimentPR = promptReports?.find((r) => r.report_type === "SENTIMENT");
          const isActive = activeTab === ("SENTIMENT" as PromptReportType);
          const isReady = sentimentPR?.status === "PROCESSED";
          const isProcessing = sentimentPR?.status === "PROCESSING" || sentimentPR?.status === "CREATED";
          const showDot = !isActive && isUpdated("SENTIMENT", sentimentPR?.updated_at ?? job?.created_at);
          return (
            <>
              <div className="w-px h-5 bg-border self-center mx-1" />
              <button
                data-qa="report-tab-SENTIMENT"
                onClick={() => {
                  markViewed("SENTIMENT", sentimentPR?.updated_at);
                  setActiveTab("SENTIMENT" as PromptReportType);
                }}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors inline-flex items-center ${
                  isActive
                    ? "bg-foreground text-background font-medium"
                    : isReady
                      ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                      : "border border-dashed border-primary/40 text-primary/70 hover:bg-primary/5"
                }`}
              >
                {isProcessing && <Loader2 className="w-3 h-3 animate-spin inline mr-1" />}
                {!isReady && !isProcessing && <span className="mr-1 text-xs">&#10022;</span>}
                Sentiment{sourceLangCode ? ` (${sourceLangCode})` : ""}
                {showDot && <span className="w-2 h-2 rounded-full bg-green-500 ml-1.5 shrink-0" />}
              </button>
            </>
          );
        })()}
      </div>

      {/* Source-language notice — transcript & sentiment stay in the original language */}
      {showSourceLangNotice && (
        <div className="px-6 pt-2 shrink-0">
          <p className="text-xs text-muted-foreground bg-muted/50 border rounded-md px-3 py-1.5 inline-flex items-center gap-1.5">
            <Info className="w-3.5 h-3.5 shrink-0" />
            Transcript and sentiment are always in the original language ({sourceLangCode}).
          </p>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4" data-qa="report-content">
        {/* Job processing card (transcription / analysis still running) */}
        {isJobActive && job && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2.5">
                {job.type === "VIDEO" ? (
                  <FileVideo className="w-5 h-5 text-primary" />
                ) : (
                  <FileAudio className="w-5 h-5 text-primary" />
                )}
                <CardTitle className="text-base font-medium" data-qa="processing-status">
                  {getStepLabel(job.step)}
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm h-5">
                <FunnyLoader />
              </p>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-muted-foreground">Transcribing & analyzing</span>
                  <span className="text-xs text-muted-foreground tabular-nums">{getStepProgress(job.step)}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-500"
                    style={{ width: `${getStepProgress(job.step)}%` }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Generation progress card (job done, individual reports still generating) */}
        {!isJobActive && isGenerating && (
              <Card data-qa="report-generating">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2.5">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    <CardTitle className="text-base font-medium">
                      {currentlyGenerating
                        ? `Generating ${tabLabel(currentlyGenerating.report_type)}…`
                        : "Generating reports…"}
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs text-muted-foreground">
                        {readyReports} of {totalReports} ready
                      </span>
                      <span className="text-xs text-muted-foreground tabular-nums">{overallProgress}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-primary transition-all duration-500"
                        style={{ width: `${overallProgress}%` }}
                      />
                    </div>
                  </div>

                  <ul className="space-y-1.5">
                    {personaTabs.map(({ type, label }) => {
                      const pr = promptReports?.find((r) => r.report_type === type);
                      if (!pr) return null;
                      let status: React.ReactNode;
                      if (pr.status === "PROCESSED") {
                        status = <span className="text-xs text-green-700">Ready</span>;
                      } else if (pr.status === "PROCESSING") {
                        status = (
                          <span className="text-xs text-primary inline-flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Generating
                          </span>
                        );
                      } else if (pr.status === "FAILED") {
                        status = <span className="text-xs text-destructive">Failed</span>;
                      } else {
                        status = <span className="text-xs text-muted-foreground">Queued</span>;
                      }
                      return (
                        <li key={type} className="flex items-center justify-between text-sm">
                          <span className={pr.status === "PROCESSED" ? "text-foreground" : "text-muted-foreground"}>
                            {label}
                          </span>
                          {status}
                        </li>
                      );
                    })}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* "The Gist" — plain-language brief shown above the Summary report */}
            {activeTab === "SUMMARY" && gistText && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3" data-qa="report-gist">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <span className="text-xs font-medium uppercase tracking-wide text-primary">The Gist</span>
                </div>
                <p className="text-sm leading-relaxed text-foreground/80">{gistText}</p>
              </div>
            )}

            {/* Failed report — show warning + Retry (persona tabs only) */}
            {activeTab !== ("SENTIMENT" as PromptReportType) && isActivePRFailed ? (
              <Card className="border-destructive/40" data-qa="report-failed">
                <CardContent className="py-10 flex flex-col items-center text-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-destructive" />
                  {(() => {
                    const failure = describeReportFailure(activePR?.error_message);
                    return (
                      <>
                        <p className="text-sm font-medium text-foreground">{failure.title}</p>
                        {failure.detail && (
                          <p className="text-xs text-muted-foreground max-w-md">{failure.detail}</p>
                        )}
                      </>
                    );
                  })()}
                  <Button
                    variant="outline"
                    size="sm"
                    data-qa="report-retry"
                    disabled={regenerate.isPending}
                    onClick={() =>
                      job && regenerate.mutate({ jobId: job.id, reportType: activeTab, language: selectedLanguage })
                    }
                  >
                    {regenerate.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCw className="w-4 h-4" />
                    )}
                    Retry
                  </Button>
                </CardContent>
              </Card>
            ) :
            /* Prompt report content (markdown renderer for persona reports; SENTIMENT has its own panel below) */
            promptReportDetail?.content && activeTab !== ("SENTIMENT" as PromptReportType) ? (
              <Card>
                <CardHeader className="pb-3 flex flex-row items-center justify-between">
                  <CardTitle className="text-lg">{tabLabel(activeTab)}</CardTitle>
                  <div className="flex items-center gap-1.5">
                    <CopyButton content={promptReportDetail.content} />
                    <PDFButton
                      content={promptReportDetail.content}
                      title={`${projectName} — ${tabLabel(activeTab)}`}
                      fileBase={`${safeName}_${tabLabel(activeTab).replace(/\s+/g, "_")}`}
                      projectTitle={projectName}
                      meta={{
                        reportType: activeTab,
                        reportLabel: tabLabel(activeTab),
                        personaLabel,
                        reportNumber: reportNumberFor(activeTab),
                        reportTotal,
                        gist: activeTab === "SUMMARY" ? (gistText ?? undefined) : undefined,
                      }}
                    />
                    <DOCXButton reportId={promptReportDetail.id} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none">
                    <Markdown
                      content={
                        activeTab === "TRANSCRIPT"
                          ? applySpeakerNames(promptReportDetail.content, refineContext?.speaker_names)
                          : promptReportDetail.content
                      }
                    />
                  </div>
                </CardContent>
              </Card>
            ) : activeTab !== ("SENTIMENT" as PromptReportType) && isActivePRSlow ? (
              <Card>
                <CardContent className="py-10 flex flex-col items-center text-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">
                    Taking longer than expected. The report is still being generated.
                  </p>
                </CardContent>
              </Card>
            ) : isLoadingPromptReport && !isGenerating && activeTab !== ("SENTIMENT" as PromptReportType) ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : promptReports && promptReports.length === 0 && report?.summary ? (
              /* Fallback: show old-style summary if no prompt reports exist */
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Executive summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-foreground/80 leading-relaxed whitespace-pre-line">
                    {report.summary.summary}
                  </p>
                </CardContent>
              </Card>
            ) : null}

        {/* Sentiment Analysis — on-demand panel */}
        {activeTab === ("SENTIMENT" as PromptReportType) && (() => {
          const sentimentPR = promptReports?.find((r) => r.report_type === "SENTIMENT");
          const sentimentDetail = activeTab === ("SENTIMENT" as PromptReportType) ? promptReportDetail : null;

          if (!sentimentPR || sentimentPR.status === "FAILED") {
            return job ? <SentimentQuiz jobId={job.id} /> : null;
          }
          if (sentimentPR.status === "PROCESSING" || sentimentPR.status === "CREATED") {
            return (
              <Card>
                <CardContent className="py-12 flex flex-col items-center gap-3">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  <p className="text-sm text-muted-foreground">Analyzing emotional patterns...</p>
                </CardContent>
              </Card>
            );
          }
          if (sentimentPR.status === "PROCESSED" && sentimentDetail?.content) {
            return <SentimentReport content={sentimentDetail.content} focusMode={sentimentPR.focus_mode} />;
          }
          return null;
        })()}

        {/* First-time discovery: while the project has a single persona, offer the next one.
            From the second persona on, the strip's "+ Add persona" chip carries the job. */}
        {job?.status === "COMPLETED" && (variants?.length ?? 0) <= 1 && activeTab !== "TRANSCRIPT" && (
          <PersonaInvite
            addable={addable ?? []}
            currentVertical={variants?.find((v) => v.persona === currentPersona)?.vertical ?? ""}
            onPick={(roleSlug) => setPersona.mutate({ jobId: job.id, roleSlug })}
            disabled={setPersona.isPending}
            onOpenAll={() => document.getElementById("persona-strip-add")?.click()}
          />
        )}
          </div>

      {/* Chat stub */}
      <div className="px-6 py-3 border-t shrink-0">
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-muted/50 border">
          <input
            type="text"
            placeholder="Ask anything about this project..."
            disabled
            className="flex-1 bg-transparent text-sm text-muted-foreground placeholder:text-muted-foreground/60 outline-none cursor-not-allowed"
          />
          <span className="text-[11px] px-2 py-0.5 rounded bg-muted text-muted-foreground font-medium shrink-0">
            Coming soon
          </span>
        </div>
      </div>
      </div>

      {job?.status === "COMPLETED" && jobId && (
        <RefinePanel
          jobId={jobId}
          currentRoleSlug={job?.role_slug ?? null}
          currentRoleName={job?.role_name ?? null}
          currentScenarioId={job?.scenario ?? null}
          currentBriefData={job?.brief_data ?? {}}
          reportCount={job?.report_count ?? 0}
          speakerCount={jobInfo?.speaker_count ?? 0}
          detectedLanguage={job?.detected_language ?? jobInfo?.language ?? ""}
          currentReportType={activeTab}
          isOpen={refineOpen}
          onClose={() => setRefineOpen(false)}
          onRefineStarted={(outputLanguage) => {
            // Point the report list at the language the refine just generated, otherwise
            // a stale ?language= filter would hide the new PROCESSING reports (no cascade).
            // `undefined` means the refine did not name a language, so the job keeps the one
            // it had — follow it rather than pinning to the source-language variant.
            setSelectedLanguage(outputLanguage);
            setHasAutoSwitched(false);
          }}
        />
      )}

      {job?.status === "COMPLETED" && jobId && (
        <ChatPanel
          jobId={jobId}
          activeTab={activeTab}
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
        />
      )}

      {job?.status === "COMPLETED" && jobId && (
        <ProjectTreePanel
          jobId={jobId}
          currentPersona={job?.persona ?? ""}
          isOpen={mapOpen}
          onClose={() => setMapOpen(false)}
          onSelectReport={(rt, persona) => {
            // Clicking a report in another persona switches the job to that persona
            // first (no-op when it already matches), then opens the report tab.
            if (persona && persona !== job?.persona) {
              handlePersonaChange(persona as Persona);
            }
            setActiveTab(rt);
            setHasAutoSwitched(true);
          }}
        />
      )}

      {job && (
        <ProjectInfoModal job={job} open={infoOpen} onOpenChange={setInfoOpen} />
      )}
    </div>
  );
}
